import type { ComponentPublicInstance, Ref } from 'vue';
import type { FlattenedItem, NestedSelectMenuItem, NestedSelectMenuItems } from './types';
import { callOrReturn } from '#layers/base/public';
import { getOutsideEventTarget } from '#layers/ui/utils/outside-interaction';

export function getChildren(item: NestedSelectMenuItem): NestedSelectMenuItem[] {
	if (!item.children) return [];
	return callOrReturn(item.children);
}

export function hasChildren(item: NestedSelectMenuItem): boolean {
	return Boolean(item.children);
}

export function setExpandedItemRef(
	expandedItem: Ref<NestedSelectMenuItem | null>,
	expandedItemRef: Ref<HTMLElement | null>,
	el: Element | ComponentPublicInstance | null,
	item: NestedSelectMenuItem,
) {
	if (expandedItem.value?.id === item.id && el) {
		expandedItemRef.value = '$el' in el ? (el.$el as HTMLElement) : (el as HTMLElement);
	}
}

export function preventOutsideDismissWithinMenu(
	event: Event,
	isTargetWithinMenu: (target: EventTarget | null) => boolean,
) {
	if (isTargetWithinMenu(getOutsideEventTarget(event))) {
		event.preventDefault();
	}
}

export function stopInputBlurDismissWithinMenu(
	event: FocusEvent,
	isTargetWithinMenu: (target: EventTarget | null) => boolean,
) {
	if (isTargetWithinMenu(event.relatedTarget)) {
		event.stopImmediatePropagation();
	}
}

export function prioritizeTopLevelMatches<T>(items: T[], isTopLevel: (item: T) => boolean): T[] {
	const topLevel: T[] = [];
	const nested: T[] = [];

	for (const item of items) {
		if (isTopLevel(item)) {
			topLevel.push(item);
		}
		else {
			nested.push(item);
		}
	}

	if (nested.length === 0 || topLevel.length === 0) return items;

	return [...topLevel, ...nested];
}

export function flattenWithPaths(
	items: NestedSelectMenuItems,
	maxDepth: number,
	currentPath: NestedSelectMenuItem[] = [],
): FlattenedItem[] {
	// Normalize grouped items to flat array
	const flatItems = Array.isArray(items[0]) ? (items as NestedSelectMenuItem[][]).flat() : items as NestedSelectMenuItem[];

	const result: FlattenedItem[] = [];

	for (const item of flatItems) {
		// Prevent immediate recursion when the same item instance is reused in its own descendant tree.
		// Repeated ids are still valid for recursive schemas where each level creates a distinct item instance.
		if (currentPath.includes(item)) continue;

		const newPath = [...currentPath, item];
		const pathLabels = newPath.map((i) => i.label);
		const pathSearchTexts = newPath.map((entry) => entry.searchText ?? entry.label);
		const flattenedItem = {
			item,
			path: newPath,
			pathIds: newPath.map((i) => i.id),
			pathLabels,
			searchText: pathSearchTexts.join(' '),
		} satisfies FlattenedItem;

		if (item.children) {
			if (item.selectable) {
				result.push(flattenedItem);
			}

			if (currentPath.length >= maxDepth) continue;

			try {
				const children = typeof item.children === 'function'
					? item.children()
					: item.children;

				result.push(...flattenWithPaths(children, maxDepth, newPath));
			}
			catch (error) {
				console.error('Failed to flatten children for item:', item.id, error);
			}
		}
		else {
			result.push(flattenedItem);
		}
	}

	return result;
}
